import React from "react";
import { Box, Typography, Modal, ModalDialog, Button } from "@mui/joy";
import { RotateCw, FileDown, Eye } from "lucide-react";
import {
    DocumentPreviewWidgetV2,
    ContractPreview,
    type IMenuAction,
} from "../../";
import {
    useDocument,
    useAsset,
    useReport,
} from "../../../hooks/query";
import { useNotification } from "../../../hooks/useNotification";
import { useProject } from "../../../hooks";
import { useContractSigning } from "./hooks";

export function ContractReview() {
    const { project } = useProject();

    const {
        contractDocument,
        workOrderDocument,
        purchaseOrderDocument,
        hasGutterWork,
        refreshContract,
        triggerSigning,
        triggerEmbeddedSigning,
        isTriggeringSigning,
        isGettingEmbeddedSignLink,
    } = useContractSigning();

    const {
        useGenerateContract,
        usePreviewContract,
        useWorkOrderCreate,
        useSupplyOrderCreate,
    } = useDocument();
    const { useGeneratePreCannedReport } = useReport();
    const { useDownload } = useAsset();
    const { showSuccess, showError } = useNotification();

    const generateContractSvc = useGenerateContract();
    const previewContractSvc = usePreviewContract();
    const generateWorkOrderSvc = useWorkOrderCreate();
    const generateSupplyOrderSvc = useSupplyOrderCreate();
    const generateGutterReportSvc = useGeneratePreCannedReport();
    const downloadAssetSvc = useDownload();

    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [previewPdfUrl, setPreviewPdfUrl] = React.useState<string | null>(
        null,
    );
    const [previewError, setPreviewError] = React.useState<string | null>(null);
    const [signLink, setSignLink] = React.useState<string | null>(null);

    const revokePreviewUrl = React.useCallback(() => {
        setPreviewPdfUrl((current) => {
            if (current) URL.revokeObjectURL(current);
            return null;
        });
    }, []);

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setPreviewError(null);
        revokePreviewUrl();
    };

    const handleOpenPreview = async () => {
        if (!project?.id) return;
        setPreviewOpen(true);
        setPreviewError(null);
        revokePreviewUrl();
        try {
            const result = await previewContractSvc.mutateAsync({
                ProjectId: project.id,
            });
            setPreviewPdfUrl(result.blobUrl);
        } catch (e: any) {
            console.error(e);
            const message =
                e?.message || "Failed to render contract preview PDF";
            setPreviewError(message);
            showError(message);
        }
    };

    const handleRegenerateContract = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateContractSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Contract regenerated successfully");
            setTimeout(() => {
                refreshContract();
            }, 1000);
            // Refresh preview if open
            if (previewOpen) {
                revokePreviewUrl();
                setPreviewError(null);
                try {
                    const result = await previewContractSvc.mutateAsync({
                        ProjectId: project.id,
                    });
                    setPreviewPdfUrl(result.blobUrl);
                } catch (e: any) {
                    setPreviewError(
                        e?.message || "Failed to refresh contract preview",
                    );
                }
            }
        } catch (error) {
            console.error("Error regenerating contract:", error);
            showError("Failed to regenerate contract");
        }
    };

    const handleSendForSigning = async () => {
        try {
            await triggerSigning();
            showSuccess("Contract sent for signing via BoldSign successfully");
            handleClosePreview();
        } catch (error: any) {
            console.error("Error sending contract for signing:", error);
            showError(error.message || "Failed to send contract for signing");
        }
    };

    const handleSignNow = async () => {
        try {
            const link = await triggerEmbeddedSigning();
            if (link) {
                setSignLink(link);
            }
        } catch (error: any) {
            console.error("Error starting embedded signing:", error);
            showError(error.message || "Failed to start embedded signing");
        }
    };

    const handleGenerateWorkOrder = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateWorkOrderSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Work Order generated successfully");
            setTimeout(() => {
                refreshContract();
            }, 1000);
        } catch (error) {
            console.error("Error generating work order:", error);
            showError("Failed to generate work order");
        }
    };

    const handleGeneratePurchaseOrder = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateSupplyOrderSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Purchase Order generated successfully");
            setTimeout(() => {
                refreshContract();
            }, 1000);
        } catch (error) {
            console.error("Error generating purchase order:", error);
            showError("Failed to generate purchase order");
        }
    };

    const handleGenerateGutterReport = async () => {
        try {
            if (!project?.id) return;
            await generateGutterReportSvc.mutateAsync({
                ReportType: "GUTTER_REPORT",
                ProjectId: project.id,
            });
            showSuccess("Gutter Report downloaded successfully");
        } catch (error) {
            console.error("Error generating gutter report:", error);
            showError("Failed to generate gutter report");
        }
    };

    const handleDownloadContract = async () => {
        if (!contractDocument?.DocumentAssetId) {
            showError("Contract PDF not available yet");
            return;
        }
        try {
            await downloadAssetSvc.mutateAsync({
                AssetId: contractDocument.DocumentAssetId,
            });
            showSuccess("Contract downloaded successfully");
        } catch (error) {
            console.error("Error downloading contract:", error);
            showError("Failed to download contract");
        }
    };

    const contractStatus = contractDocument?.Status;
    const contractMenuActions: IMenuAction[] = contractDocument
        ? [
              ...(contractDocument.DocumentAssetId
                  ? [
                        {
                            label: "Download",
                            icon: <FileDown size={16} />,
                            onClick: handleDownloadContract,
                            disabled: downloadAssetSvc.isPending,
                        },
                    ]
                  : []),
              {
                  label: "Preview",
                  icon: <Eye size={16} />,
                  onClick: handleOpenPreview,
              },
              {
                  label: "Regenerate",
                  icon: <RotateCw size={16} />,
                  onClick: handleRegenerateContract,
                  disabled: generateContractSvc.isPending,
              },
          ]
        : [];

    const workOrderMenuActions: IMenuAction[] = workOrderDocument
        ? [
              {
                  label: "Regenerate",
                  icon: <RotateCw size={16} />,
                  onClick: handleGenerateWorkOrder,
                  disabled: generateWorkOrderSvc.isPending,
              },
          ]
        : [];

    const purchaseOrderMenuActions: IMenuAction[] = purchaseOrderDocument
        ? [
              {
                  label: "Regenerate",
                  icon: <RotateCw size={16} />,
                  onClick: handleGeneratePurchaseOrder,
                  disabled: generateSupplyOrderSvc.isPending,
              },
          ]
        : [];

    return (
        <Box mt={4} display="flex" flexDirection="column" gap={4}>
            {/* Contract Section */}
            <Box>
                <Typography
                    level={"body-sm"}
                    sx={{ color: "text.primary", fontWeight: 600 }}
                    gutterBottom
                >
                    Documents Review
                </Typography>
                <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                    Formal agreement to define project deliverables, timelines,
                    and obligations.
                </Typography>
                <Box
                    mt={2}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(auto-fill, minmax(280px, 1fr))",
                        },
                        gap: 2.5,
                    }}
                >
                    <DocumentPreviewWidgetV2
                        title="Contract.pdf"
                        description={
                            contractStatus
                                ? `Project agreement and terms · ${contractStatus}`
                                : "Project agreement and terms"
                        }
                        fileType="PDF"
                        assetId={contractDocument?.DocumentAssetId}
                        onButtonClick={handleOpenPreview}
                        loading={
                            generateContractSvc.isPending ||
                            previewContractSvc.isPending ||
                            downloadAssetSvc.isPending
                        }
                        menuActions={contractMenuActions}
                    />
                    <DocumentPreviewWidgetV2
                        title="Work Order.pdf"
                        description={
                            workOrderDocument
                                ? `Scope and materials for the crew · ${workOrderDocument.Status}`
                                : "Click to generate work order"
                        }
                        fileType="PDF"
                        assetId={workOrderDocument?.DocumentAssetId}
                        onButtonClick={
                            workOrderDocument
                                ? undefined
                                : handleGenerateWorkOrder
                        }
                        loading={generateWorkOrderSvc.isPending}
                        menuActions={workOrderMenuActions}
                    />
                    <DocumentPreviewWidgetV2
                        title="Purchase Order.pdf"
                        description={
                            purchaseOrderDocument
                                ? `Material supply order request · ${purchaseOrderDocument.Status}`
                                : "Click to generate purchase order"
                        }
                        fileType="PDF"
                        assetId={purchaseOrderDocument?.DocumentAssetId}
                        onButtonClick={
                            purchaseOrderDocument
                                ? undefined
                                : handleGeneratePurchaseOrder
                        }
                        loading={generateSupplyOrderSvc.isPending}
                        menuActions={purchaseOrderMenuActions}
                    />
                    {hasGutterWork && (
                        <DocumentPreviewWidgetV2
                            title="Gutter Work Order.pdf"
                            description="Gutter work order and invoice report"
                            fileType="PDF"
                            onButtonClick={handleGenerateGutterReport}
                            loading={generateGutterReportSvc.isPending}
                        />
                    )}
                </Box>
            </Box>

            <ContractPreview
                open={previewOpen}
                onClose={handleClosePreview}
                pdfUrl={previewPdfUrl}
                isLoadingPreview={previewContractSvc.isPending}
                previewError={previewError}
                isGenerating={generateContractSvc.isPending}
                onGenerate={async () => {
                    await handleRegenerateContract();
                }}
                onSend={handleSendForSigning}
                isSending={isTriggeringSigning}
                isSendDisabled={
                    !contractDocument || contractDocument?.Status === "INIT"
                }
                onDownload={handleDownloadContract}
                isDownloading={downloadAssetSvc.isPending}
                isDownloadDisabled={!contractDocument?.DocumentAssetId}
                onSignNow={handleSignNow}
                isSigningNow={isTriggeringSigning || isGettingEmbeddedSignLink}
            />

            {/* Embedded Signing Modal */}
            <Modal open={!!signLink} onClose={() => setSignLink(null)}>
                <ModalDialog
                    variant="outlined"
                    sx={{
                        width: "min(1000px, 94vw)",
                        maxWidth: 1000,
                        height: "90vh",
                        p: 0,
                        overflow: "hidden",
                        borderRadius: "4px",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1,
                            px: 2,
                            pt: 1.75,
                            pb: 1,
                            bgcolor: "background.surface",
                        }}
                    >
                        <Box sx={{ minWidth: 0, pr: 2 }}>
                            <Typography level="title-md" sx={{ fontWeight: 700 }}>
                                Sign Contract
                            </Typography>
                            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                                Please review and sign the document below.
                            </Typography>
                        </Box>
                        <Button
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={() => setSignLink(null)}
                            sx={{ mt: -0.5, mr: -0.5 }}
                        >
                            Close
                        </Button>
                    </Box>
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", borderTop: "1px solid", borderColor: "divider" }}>
                        {signLink && (
                            <iframe
                                src={signLink}
                                style={{ width: "100%", height: "100%", border: "none", flex: 1 }}
                                title="Sign Contract"
                            />
                        )}
                    </Box>
                </ModalDialog>
            </Modal>
        </Box>
    );
}
